import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import FirearmSpecificTable from '../../../components/tables/FirearmSpecificTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useFirearmSpecificSearch, useDeleteFirearmSpecific } from '../../../hooks/useFirearmSpecificApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  FirearmSpecificDTO,
  FirearmTypeDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useFirearmTypes } from '../../../hooks/useFirearmTypeApi';

const FirearmSpecificList: React.FC = () => {
  const { search } = useFirearmSpecificSearch();
  const deleteFirearmSpecific = useDeleteFirearmSpecific();
  const { data: firearmTypes = [] } = useFirearmTypes();
  const [items, setItems] = useState<FirearmSpecificDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Firearm Specifics', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    setIsLoading(true);
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching FirearmSpecifics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: FirearmSpecificDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteFirearmSpecific.mutateAsync(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting FirearmSpecific:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/firearmspecifics"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.FirearmSpecific.View"
    >
      <Card
        title="Manage Firearm Specifics"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/firearmspecifics/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Enter Title"
            />
            <FormField
              id="firearmTypeId"
              label="Select a Firearms Type"
              type="dropdown"
              value={searchFilters.firearmTypeId || ''}
              placeholder='All Firearms Types'
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  firearmTypeId: e.target.value,
                })
              }
              options={[
                ...firearmTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                })),
              ]}
            />
          </div>
          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <FirearmSpecificTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/firearmspecifics/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default FirearmSpecificList;
